import { ConfigProvider, theme as antdTheme } from "antd";
import ru from "antd/locale/ru_RU";
import { type ReactElement, type ReactNode, useEffect } from "react";
import { useAppSelector } from "../../store/reduxHooks";

const sharedComponents = {
  Button: {
    algorithm: true,
    colorText: "#3C79FF",
    colorBorder: "#3C79FF",
    defaultHoverBorderColor: "#6B9AFF",
    defaultHoverColor: "#6B9AFF",
  },
  Table: {
    algorithm: true,
  },
};

const lightTheme = {
  algorithm: antdTheme.defaultAlgorithm,
  cssVar: true,
  token: {
    colorPrimary: "#3C79FF",
    colorBgLayout: "#f5f5f5",
    colorBgContainer: "#ffffff",
    colorBgElevated: "#ffffff",
    colorText: "rgba(0, 0, 0, 0.88)",
    /* Как --app-text-muted в index.css */
    colorTextSecondary: "rgba(0, 0, 0, 0.50)",
    colorBorder: "rgba(0, 0, 0, 0.18)",
  },
  components: {
    ...sharedComponents,
    Button: {
      ...sharedComponents.Button,
      borderColorDisabled: "rgba(0, 0, 0, 0.10)",
    },
    Tag: {
      defaultColor: "rgba(0, 0, 0, 0.4)",
      defaultBg: "rgb(243, 243, 243)",
    },
    Typography: {
      colorText: "rgba(0, 0, 0, 0.88)",
    },
    Table: {
      ...sharedComponents.Table,
      rowHoverBg: "rgba(217, 217, 217, 0.36)",
    },
  },
  hashed: false,
};

const darkTheme = {
  algorithm: antdTheme.darkAlgorithm,
  cssVar: true,
  token: {
    colorPrimary: "#6B9AFF",
    colorBgLayout: "#0f1115",
    colorBgContainer: "#141820",
    colorBgElevated: "#171b24",
    /* Как --app-text / --app-text-muted в index.css */
    colorText: "rgba(255, 255, 255, 0.94)",
    colorTextSecondary: "rgba(255, 255, 255, 0.74)",
    colorBorder: "rgba(255, 255, 255, 0.26)",
  },
  components: {
    ...sharedComponents,
    Button: {
      ...sharedComponents.Button,
      borderColorDisabled: "rgba(255, 255, 255, 0.15)",
      colorText: "#6B9AFF",
      colorBorder: "#6B9AFF",
      defaultHoverBorderColor: "#8fb4ff",
      defaultHoverColor: "#8fb4ff",
    },
    Tag: {
      defaultColor: "rgba(255, 255, 255, 0.65)",
      defaultBg: "rgba(255, 255, 255, 0.12)",
    },
    Typography: {
      colorText: "rgba(255, 255, 255, 0.88)",
    },
    Table: {
      ...sharedComponents.Table,
      rowHoverBg: "rgba(255, 255, 255, 0.08)",
    },
  },
  hashed: false,
};

export function ThemeConfigProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const isDark = useAppSelector((s) => s.themeSlice.isDark);

  useEffect(() => {
    document.body.dataset.theme = isDark ? "dark" : "light";
  }, [isDark]);

  return (
    <ConfigProvider locale={ru} theme={isDark ? darkTheme : lightTheme}>
      {children}
    </ConfigProvider>
  );
}
